// A Takt "train": a user-authored SEQUENCE of work — ordered elements (a work
// code + duration each) plus the internal (same-train) dependencies between
// them. Saved as an opaque top-level `train` key in the pull-plan draft payload
// (sibling to proto_board, read like `mode`/`kind`, NOT part of the strict
// DraftPayload ops interface).
//
// A train is a TEMPLATE; the real activities that follow it are INSTANCES. The
// link is by IDENTITY, not by work code: each instance is a map { elementId ->
// activityRef } recorded when the user selects a chunk and maps it. So duplicate
// work codes are fine — each activity is mapped to one element by the user's
// selection, never guessed. `apply` then SYNCS every instance's durations +
// internal ties to the template; cross-instance / external ties are untouched.
import { makeId } from "../../utils/id";

/** One element in a train: a work code + duration, in canonical order.
 *  Identified by a stable client id (never an activity id) — that id IS the
 *  template identity an instance maps to. */
export interface TrainCar {
  /** Stable client id (mint via makeId("car")). The element identity. */
  carId: string;
  /** The work code this element carries (display + the line-up check on map). */
  code: string;
  /** Duration in board-days (>= 1). */
  durationDays: number;
  /** The duration captured when the train was built/seeded. Apply compares
   *  `durationDays` against this to tell a USER edit from an instance bar merely
   *  differing from the template — never moved by the duration mutators. A car is
   *  "duration-edited" iff durationDays !== baselineDurationDays. */
  baselineDurationDays: number;
  /** STATIC visual position on the train lane (day index) — used in "board" mode
   *  so editing durations/ties doesn't make bars jump. "Forward pass" mode
   *  positions by simulateTrain() instead. */
  startIndex: number;
  /** Lock state of the SOURCE activity this car was captured from (completed /
   *  in-progress). Threaded so findTrainInLeaf can pair a leaf bar to the car of
   *  MATCHING status when a code carries both a locked and an active occurrence —
   *  otherwise the active edit can land on the locked bar. Legacy payloads / new
   *  cars default undefined (treated as unlocked). */
  locked?: boolean;
  /** Source activity was COMPLETED specifically (a stricter subset of `locked`).
   *  An editable car (not-started OR in-progress) must never bind a COMPLETED leaf
   *  bar — that would strand its edit on finished work. Only a completed car may. */
  completed?: boolean;
}

export type TrainDepType = "FS" | "SS" | "FF" | "SF";

/** An internal tie BETWEEN two elements, by carId — the ONLY ties a train
 *  stores. Mirrored onto each instance on apply. */
export interface TrainDep {
  predCarId: string;
  succCarId: string;
  /** Defaults "FS". */
  type: TrainDepType;
  lagDays: number;
}

/** One mapped instance: element carId -> the activity ref it governs on that
 *  chunk (a real activity id as a string, or a draft_id for a new activity). */
export type TrainInstance = Record<string, string>;

export interface TrainState {
  /** Elements in canonical left-to-right order. */
  cars: TrainCar[];
  /** Internal ties (element -> element only). */
  deps: TrainDep[];
  /** The ties captured when the train was built/seeded. Apply diffs `deps`
   *  against this to stage ONLY the ties the user added/removed/retyped — never
   *  moved by the dep mutators. A tie is "added" iff in deps but not baselineDeps,
   *  "removed" iff in baselineDeps but not deps, "retyped" iff in both with a
   *  different type/lag. */
  baselineDeps: TrainDep[];
  /** Mapped instances — each a { carId -> activityRef } join. Instance #0 is the
   *  chunk the train was created from; the rest are mapped chunks. */
  instances: TrainInstance[];
  /** Row activity refs orphaned by deleting an element — removed from the board
   *  on the NEXT Apply (so deleting an element defers to Apply, symmetric with
   *  adding one). Cleared by Apply. */
  pendingRowDeletes?: string[];
  status: "draft" | "ready";
  /** How the train was built:
   *   - "captured": from existing activities selected on the board. Auto-maps onto
   *     matching levels and APPLIES (syncs the matching run on each).
   *   - "fresh": built from scratch (new work). Has no Apply — you PLACE its run
   *     onto chosen levels (each placement stamps independent draft work).
   *  Absent (legacy payloads) reads as "captured". */
  kind?: "captured" | "fresh";
}

export function emptyTrain(): TrainState {
  return { cars: [], deps: [], baselineDeps: [], instances: [], status: "draft", kind: "fresh" };
}

const DEP_TYPES: TrainDepType[] = ["FS", "SS", "FF", "SF"];

/** Parse the opaque top-level `train` key from a payload_json blob. Returns null
 *  when absent or malformed (mirrors readPayloadMode). */
export function readPayloadTrain(
  payloadJson: string | null | undefined,
): TrainState | null {
  if (!payloadJson) return null;
  let data: unknown;
  try {
    data = JSON.parse(payloadJson);
  } catch {
    return null;
  }
  if (typeof data !== "object" || data === null) return null;
  const raw = (data as Record<string, unknown>).train;
  if (typeof raw !== "object" || raw === null) return null;
  const t = raw as Record<string, unknown>;

  const cars: TrainCar[] = Array.isArray(t.cars)
    ? t.cars
        .filter(
          (c): c is Record<string, unknown> =>
            typeof c === "object" && c !== null,
        )
        .filter(
          (c) => typeof c.carId === "string" && typeof c.code === "string",
        )
        .map((c, i) => {
          const durationDays =
            typeof c.durationDays === "number" && c.durationDays >= 1
              ? c.durationDays
              : 1;
          return {
            carId: c.carId as string,
            code: c.code as string,
            durationDays,
            // Legacy payloads (no baseline) degrade to "no edit": baseline = current.
            baselineDurationDays:
              typeof c.baselineDurationDays === "number" &&
              c.baselineDurationDays >= 1
                ? c.baselineDurationDays
                : durationDays,
            startIndex:
              typeof c.startIndex === "number" && c.startIndex >= 0
                ? c.startIndex
                : i,
            ...(typeof c.locked === "boolean" ? { locked: c.locked } : {}),
            ...(typeof c.completed === "boolean"
              ? { completed: c.completed }
              : {}),
          };
        })
    : [];

  const carIds = new Set(cars.map((c) => c.carId));
  const parseDeps = (raw: unknown): TrainDep[] =>
    Array.isArray(raw)
      ? raw
          .filter(
            (d): d is Record<string, unknown> =>
              typeof d === "object" && d !== null,
          )
          .filter(
            (d) =>
              typeof d.predCarId === "string" &&
              typeof d.succCarId === "string" &&
              d.predCarId !== d.succCarId &&
              carIds.has(d.predCarId as string) &&
              carIds.has(d.succCarId as string),
          )
          .map((d) => ({
            predCarId: d.predCarId as string,
            succCarId: d.succCarId as string,
            type: DEP_TYPES.includes(d.type as TrainDepType)
              ? (d.type as TrainDepType)
              : "FS",
            lagDays: typeof d.lagDays === "number" ? d.lagDays : 0,
          }))
      : [];
  // Fold parsed deps through addTrainDep so a persisted/legacy payload carrying a
  // cycle (e.g. opposing FS/SS between one pair) degrades to an acyclic, no-op-safe
  // train — simulateTrain only converges on acyclic deps, and the back-edge is
  // dropped the same way an interactive edit would reject it.
  const acyclic = (raw: TrainDep[]): TrainDep[] =>
    raw.reduce<TrainDep[]>(
      (acc, d) => addTrainDep(acc, d.predCarId, d.succCarId, d.type, d.lagDays),
      [],
    );
  const deps = acyclic(parseDeps(t.deps));
  // Legacy payloads (no baselineDeps) degrade to "no edit": baseline = deps.
  const baselineDeps =
    t.baselineDeps === undefined ? deps : acyclic(parseDeps(t.baselineDeps));

  const instances: TrainInstance[] = Array.isArray(t.instances)
    ? t.instances
        .filter((m): m is Record<string, unknown> => typeof m === "object" && m !== null)
        .map((m) => {
          const inst: TrainInstance = {};
          for (const [carId, ref] of Object.entries(m)) {
            if (carIds.has(carId) && typeof ref === "string") inst[carId] = ref;
          }
          return inst;
        })
        .filter((inst) => Object.keys(inst).length > 0)
    : [];

  const pendingRowDeletes = Array.isArray(t.pendingRowDeletes)
    ? t.pendingRowDeletes.filter((r): r is string => typeof r === "string")
    : [];

  return {
    cars,
    deps,
    baselineDeps,
    instances,
    status: t.status === "ready" ? "ready" : "draft",
    // Legacy payloads predate the fork — every train then was capture-based.
    kind: t.kind === "fresh" ? "fresh" : "captured",
    ...(pendingRowDeletes.length > 0 ? { pendingRowDeletes } : {}),
  };
}

/** Plain serializable form for the autosave payload (identity — TrainState is
 *  already a plain object). */
export function serializeTrain(t: TrainState): TrainState {
  return t;
}

/** Every activity ref mapped to the train, across all instances (for the
 *  board's "this is the train" highlight). */
export function mappedRefs(train: TrainState | null): Set<string> {
  const out = new Set<string>();
  if (!train) return out;
  for (const inst of train.instances) {
    for (const ref of Object.values(inst)) out.add(ref);
  }
  return out;
}

/** Simulate the train onto a 0-based day axis: a small forward pass over the
 *  durations + internal ties (FS/SS/FF/SF + lag). Elements with no predecessor
 *  start at day 0. Used to draw the train's gantt — no real dates needed. */
export function simulateTrain(
  train: TrainState,
): Map<string, { startDay: number; finishDay: number }> {
  const dur = new Map(
    train.cars.map((c) => [c.carId, Math.max(1, Math.round(c.durationDays || 1))]),
  );
  // The bounded fixpoint below (pass <= cars.length) only converges on an ACYCLIC
  // dep set. The two non-interactive constructors don't guarantee that, so own the
  // contract here: drop any dep that would close a loop given the ones already
  // kept, degrading an accidental cycle to a stable day-0 layout.
  const acyclicDeps = train.deps.reduce<TrainDep[]>(
    (acc, d) =>
      trainWouldCycle(acc, d.predCarId, d.succCarId) ? acc : [...acc, d],
    [],
  );
  const predsBySucc = new Map<string, TrainDep[]>();
  for (const d of acyclicDeps) {
    (predsBySucc.get(d.succCarId) ?? predsBySucc.set(d.succCarId, []).get(d.succCarId)!).push(d);
  }
  const start = new Map<string, number>();
  // Finish-pinned ties (FF/SF) constrain the successor's FINISH, not its start.
  // Track the finish driven by START constraints (FS/SS, no day-0 floor) and the
  // finish-pin separately so the day-0 start floor never inflates a pinned finish.
  const startFinish = new Map<string, number>();
  const finishPin = new Map<string, number>();
  // A car's TRUE finish (start+dur, stretched out by any finish-pin). FS/FF
  // successors must drive off this, not the bare start+dur — otherwise a car
  // stretched by an FF/SF pin hands its successors a too-early finish.
  const trueFinish = new Map<string, number>();
  const finishOf = (carId: string): number =>
    trueFinish.get(carId) ?? (start.get(carId) ?? 0) + (dur.get(carId) ?? 1);
  for (let pass = 0; pass <= train.cars.length; pass++) {
    let changed = false;
    for (const car of train.cars) {
      const cd = dur.get(car.carId) ?? 1;
      let s = 0;
      let hasStartCstr = false;
      let pin = 0;
      let hasPin = false;
      for (const d of predsBySucc.get(car.carId) ?? []) {
        const ps = start.get(d.predCarId) ?? 0;
        const pf = finishOf(d.predCarId);
        if (d.type === "FS") {
          s = Math.max(s, pf + d.lagDays);
          hasStartCstr = true;
        } else if (d.type === "SS") {
          s = Math.max(s, ps + d.lagDays);
          hasStartCstr = true;
        } else if (d.type === "FF") {
          pin = Math.max(pin, pf + d.lagDays);
          hasPin = true;
        } else {
          pin = Math.max(pin, ps + d.lagDays); // SF: pin succ.finish to pred.start
          hasPin = true;
        }
      }
      // A finish pin implies a start (pin - duration); fold in, never below 0 —
      // BUT only when no FS/SS tie already drives the start. With a start-driver the
      // finish pin must stretch the bar's FINISH (via `s + cd` -> `pin`), not slide
      // its start forward and clobber the FS/SS-driven start.
      s = hasStartCstr ? Math.max(0, s) : Math.max(0, s, pin - cd);
      // Finish from start constraints (start + own duration); only when one drove
      // the start, so the day-0 floor alone doesn't manufacture a finish.
      const sf = hasStartCstr ? s + cd : 0;
      const tf = hasPin ? Math.max(pin, sf, s + cd) : Math.max(s + cd, sf);
      if (
        start.get(car.carId) !== s ||
        startFinish.get(car.carId) !== sf ||
        finishPin.get(car.carId) !== (hasPin ? pin : -1) ||
        trueFinish.get(car.carId) !== tf
      ) {
        start.set(car.carId, s);
        startFinish.set(car.carId, sf);
        finishPin.set(car.carId, hasPin ? pin : -1);
        trueFinish.set(car.carId, tf);
        changed = true;
      }
    }
    if (!changed) break;
  }
  const out = new Map<string, { startDay: number; finishDay: number }>();
  for (const c of train.cars) {
    const s = start.get(c.carId) ?? 0;
    const cd = dur.get(c.carId) ?? 1;
    const pin = finishPin.get(c.carId) ?? -1;
    const sf = startFinish.get(c.carId) ?? 0;
    // A finish pin can move where a bar sits but never compress it below its own
    // duration, so the natural start + duration is always folded in.
    const finish = pin >= 0 ? Math.max(pin, sf, s + cd) : Math.max(s + cd, sf);
    out.set(c.carId, { startDay: s, finishDay: finish });
  }
  return out;
}

/** True when tying predCarId -> succCarId would close a loop among the cars,
 *  given the existing deps. Walks downstream from succ; reaching pred = cycle. */
export function trainWouldCycle(
  deps: TrainDep[],
  predCarId: string,
  succCarId: string,
): boolean {
  if (predCarId === succCarId) return true;
  const succsOf = new Map<string, string[]>();
  for (const d of deps) {
    const list = succsOf.get(d.predCarId);
    if (list) list.push(d.succCarId);
    else succsOf.set(d.predCarId, [d.succCarId]);
  }
  const seen = new Set<string>([succCarId]);
  const stack = [succCarId];
  while (stack.length > 0) {
    const cur = stack.pop() as string;
    for (const nxt of succsOf.get(cur) ?? []) {
      if (nxt === predCarId) return true;
      if (!seen.has(nxt)) {
        seen.add(nxt);
        stack.push(nxt);
      }
    }
  }
  return false;
}

/** Order car ids so every FS predecessor comes before its successor. Stamping a
 *  placed run in this order guarantees each car's FS predecessor draft already
 *  exists when its tie is wired — `cars[]` order is independent of FS direction,
 *  so iterating it raw silently drops a tie whose predecessor sits later. Stable
 *  for already-ordered runs; cycle-safe (deps are kept acyclic by addTrainDep). */
export function fsPlacementOrder(cars: TrainCar[], deps: TrainDep[]): string[] {
  const ids = new Set(cars.map((c) => c.carId));
  const predsBySucc = new Map<string, string[]>();
  for (const d of deps) {
    if (d.type !== "FS" || !ids.has(d.predCarId) || !ids.has(d.succCarId)) continue;
    const list = predsBySucc.get(d.succCarId);
    if (list) list.push(d.predCarId);
    else predsBySucc.set(d.succCarId, [d.predCarId]);
  }
  const order: string[] = [];
  const seen = new Set<string>();
  const visit = (id: string) => {
    if (seen.has(id)) return;
    seen.add(id); // before recursing → a cyclic dep can't loop forever
    for (const pred of predsBySucc.get(id) ?? []) visit(pred);
    order.push(id);
  };
  for (const c of cars) visit(c.carId);
  return order;
}

/** Add an element -> element tie. No-op if it already exists or would loop. */
export function addTrainDep(
  deps: TrainDep[],
  predCarId: string,
  succCarId: string,
  type: TrainDepType = "FS",
  lagDays = 0,
): TrainDep[] {
  if (predCarId === succCarId) return deps;
  if (deps.some((d) => d.predCarId === predCarId && d.succCarId === succCarId)) {
    return deps;
  }
  if (trainWouldCycle(deps, predCarId, succCarId)) return deps;
  return [...deps, { predCarId, succCarId, type, lagDays }];
}

/** Drop the predCarId -> succCarId tie. */
export function removeTrainDep(
  deps: TrainDep[],
  predCarId: string,
  succCarId: string,
): TrainDep[] {
  return deps.filter(
    (d) => !(d.predCarId === predCarId && d.succCarId === succCarId),
  );
}

/** Retype / re-lag an existing predCarId -> succCarId tie in place. */
export function updateTrainDep(
  deps: TrainDep[],
  predCarId: string,
  succCarId: string,
  type: TrainDepType,
  lagDays: number,
): TrainDep[] {
  return deps.map((d) =>
    d.predCarId === predCarId && d.succCarId === succCarId
      ? { ...d, type, lagDays }
      : d,
  );
}

/** Re-point a tie ATOMICALLY + cycle-safe: drop (removePred→removeSucc) and add
 *  (addPred→addSucc) in one step. If the add would loop the train, the ORIGINAL
 *  deps are returned with `cycle: true` — so a caller can alert and never leaves
 *  the old tie removed (the silent-delete bug: remove-then-add where the add is
 *  rejected). Null endpoints mean "no removal" / "no addition" (pure add/remove).
 *  A same-tie replace is a retype/re-lag in place. */
export function replaceTrainDep(
  deps: TrainDep[],
  removePred: string | null,
  removeSucc: string | null,
  addPred: string | null,
  addSucc: string | null,
  type: TrainDepType = "FS",
  lagDays = 0,
): { deps: TrainDep[]; cycle: boolean } {
  if (addPred && addSucc && removePred === addPred && removeSucc === addSucc) {
    return { deps: updateTrainDep(deps, addPred, addSucc, type, lagDays), cycle: false };
  }
  let next = deps;
  if (removePred && removeSucc) next = removeTrainDep(next, removePred, removeSucc);
  if (addPred && addSucc) {
    if (addPred !== addSucc && trainWouldCycle(next, addPred, addSucc)) {
      return { deps, cycle: true };
    }
    next = addTrainDep(next, addPred, addSucc, type, lagDays);
  }
  return { deps: next, cycle: false };
}

/** Drop an element AND every tie that references it, unmap it from each
 *  instance, and remember the row activities it was driving so the next Apply
 *  removes them (deferred — editing the template never touches rows directly). */
export function removeTrainCar(state: TrainState, carId: string): TrainState {
  const orphaned: string[] = [];
  const instances = state.instances
    .map((inst) => {
      if (inst[carId]) orphaned.push(inst[carId]);
      const next = { ...inst };
      delete next[carId];
      return next;
    })
    .filter((inst) => Object.keys(inst).length > 0);
  return {
    ...state,
    cars: state.cars.filter((c) => c.carId !== carId),
    deps: state.deps.filter(
      (d) => d.predCarId !== carId && d.succCarId !== carId,
    ),
    baselineDeps: state.baselineDeps.filter(
      (d) => d.predCarId !== carId && d.succCarId !== carId,
    ),
    instances,
    pendingRowDeletes: [...(state.pendingRowDeletes ?? []), ...orphaned],
  };
}

/** Re-duration an element (board-days, clamped to >= 1). */
export function updateTrainCarDuration(
  state: TrainState,
  carId: string,
  durationDays: number,
): TrainState {
  const d = Math.max(1, Math.round(durationDays) || 1);
  return {
    ...state,
    cars: state.cars.map((c) =>
      c.carId === carId ? { ...c, durationDays: d } : c,
    ),
  };
}

/** Move an element's STATIC board position (day index, clamped >= 0). */
export function setTrainCarPosition(
  state: TrainState,
  carId: string,
  startIndex: number,
): TrainState {
  const s = Math.max(0, Math.round(startIndex) || 0);
  return {
    ...state,
    cars: state.cars.map((c) => (c.carId === carId ? { ...c, startIndex: s } : c)),
  };
}

/** Recode an element in place. */
export function updateTrainCarCode(
  state: TrainState,
  carId: string,
  code: string,
): TrainState {
  return {
    ...state,
    cars: state.cars.map((c) => (c.carId === carId ? { ...c, code } : c)),
  };
}

/** Add a new element. Appends by default (index omitted); its static position
 *  sits just past the current rightmost element, so it reads as "next in the
 *  sequence" rather than stacking on top of an existing car. New elements carry
 *  no ties or instance mappings until the user wires them. */
export function addTrainCar(
  state: TrainState,
  code: string,
  durationDays = 1,
  index?: number,
): TrainState {
  const dur = Math.max(1, Math.round(durationDays) || 1);
  const car: TrainCar = {
    carId: makeId("car"),
    code: code.trim(),
    durationDays: dur,
    // A new car lands at its create duration on every instance: baseline = dur
    // means the create's duration is authored, but a later edit still diffs.
    baselineDurationDays: dur,
    startIndex:
      state.cars.length > 0
        ? Math.max(...state.cars.map((c) => c.startIndex + c.durationDays))
        : 0,
  };
  const at =
    index == null
      ? state.cars.length
      : Math.max(0, Math.min(Math.round(index), state.cars.length));
  return {
    ...state,
    cars: [...state.cars.slice(0, at), car, ...state.cars.slice(at)],
  };
}
